import * as React from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  AI_GENERATABLE_QUESTION_TYPES,
  QUESTION_DIFFICULTY_LABEL,
  QUESTION_DIFFICULTY_ORDER,
  QUESTION_LANGUAGE_LABEL,
  QUESTION_TYPE_LABEL,
  type AssessmentGenerateQuestionsRequest,
  type AssessmentGenerateQuestionsResponse,
  type AssessmentGradeBand,
  type AssessmentQuestionPublic,
  type AssessmentTopicPublic,
  type QuestionDifficulty,
  type QuestionLanguage,
  type QuestionType,
} from "@eduviaos/shared";
import {
  useApproveAssessmentQuestion,
  useArchiveAssessmentQuestion,
  useGenerateAssessmentQuestionsBatch,
  useRegenerateAssessmentQuestion,
} from "@/hooks/useAssessments";

/**
 * Phase F11 — AI Fill dialog for the IA Question Bank.
 *
 * Surfaces the F9 AI question generation chain (Gemini → NVIDIA NIM
 * fallback) on a per-topic basis. The IA admin picks the type +
 * difficulty + grade band + count and the BE returns N persisted
 * drafts which are reviewed in-dialog and approved / regenerated /
 * archived without leaving the page.
 *
 * Why opt-in via a button (not auto-fetch):
 *   * AI calls cost real money per impression — never fire one
 *     unprompted.
 *   * The submit gesture preserves user agency ("I asked for this")
 *     vs auto-suggestions that read as "the AI did it to me".
 *
 * States, each visually distinct:
 *   1. Idle / form — fields visible, submit primary.
 *   2. Loading — spinner + animated copy ("Asking <provider>…"),
 *      submit + cancel disabled. The BE call must run to completion
 *      so the cost is correctly billed in the AI call log.
 *   3. Success — review surface listing every generated draft with
 *      per-row Approve / Regenerate / Archive affordances + the AI
 *      envelope summary (provider, latency, fallback pill, rejected
 *      counts).
 *   4. Error 503 AI_UNAVAILABLE — amber resilience panel + Retry.
 *   5. Error other — red destructive panel + Retry.
 *
 * Motion budget (Iron Rule #6 + emil-design-eng):
 *   * Dialog open 200ms ease-out (Radix default).
 *   * Generated cards stagger-in 60ms apart, max 5 staggered,
 *     total ≤ 300ms (opacity + 4px translateY only).
 *   * Loading-to-success cross-fade 180ms.
 *   * No width / height transitions; respects prefers-reduced-motion
 *     via Motion's m.* defaults.
 *
 * A11y:
 *   * Native <form role="form"> with `aria-describedby` on inputs.
 *   * <output aria-live="polite"> announces new drafts as they land.
 *   * Per-card action buttons carry explicit aria-labels referencing
 *     the row index ("Approve question #2").
 *   * Focus is trapped inside the dialog by the underlying
 *     Radix primitive.
 */

interface AIFillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  topic: AssessmentTopicPublic;
  /** Toast hook injected by the page; lets us call `toast.success(...)`
   *  without re-importing the entire UI kit here. */
  onSuccess?: (n: number) => void;
}

type DialogStage = "form" | "loading" | "review";

const DEFAULT_COUNT = 5;
const MAX_COUNT = 25;
const MIN_COUNT = 1;
const MAX_EXTRA_INSTRUCTIONS = 1000;

export function AIFillDialog({ open, onOpenChange, topic, onSuccess }: AIFillDialogProps) {
  // ── Form state ────────────────────────────────────────────────────────
  const [type, setType] = React.useState<QuestionType>("mcq_single");
  const [difficulty, setDifficulty] = React.useState<QuestionDifficulty>("medium");
  const [language, setLanguage] = React.useState<QuestionLanguage>("en");
  const [gradeMin, setGradeMin] = React.useState<number>(topic.grade_band?.min ?? 6);
  const [gradeMax, setGradeMax] = React.useState<number>(topic.grade_band?.max ?? 8);
  const [count, setCount] = React.useState<number>(DEFAULT_COUNT);
  const [marks, setMarks] = React.useState<number>(1.0);
  const [extraInstructions, setExtraInstructions] = React.useState<string>("");

  // ── Result state ──────────────────────────────────────────────────────
  const [stage, setStage] = React.useState<DialogStage>("form");
  const [result, setResult] = React.useState<AssessmentGenerateQuestionsResponse | null>(null);
  const [error, setError] = React.useState<unknown>(null);

  // Track which generated drafts the admin has approved / archived so
  // the review list keeps a coherent local order even if the BE list
  // cache invalidates underneath.
  const [approvedIds, setApprovedIds] = React.useState<Set<string>>(() => new Set());
  const [archivedIds, setArchivedIds] = React.useState<Set<string>>(() => new Set());

  // ── Mutations ─────────────────────────────────────────────────────────
  const generateMutation = useGenerateAssessmentQuestionsBatch();
  const approveMutation = useApproveAssessmentQuestion();
  const regenerateMutation = useRegenerateAssessmentQuestion();
  const archiveMutation = useArchiveAssessmentQuestion();

  // ── Validation ────────────────────────────────────────────────────────
  const formError = React.useMemo(() => {
    if (gradeMin < 1 || gradeMin > 12) return "Grade min must be between 1 and 12.";
    if (gradeMax < 1 || gradeMax > 12) return "Grade max must be between 1 and 12.";
    if (gradeMin > gradeMax) return "Grade min must be ≤ grade max.";
    if (count < MIN_COUNT || count > MAX_COUNT)
      return `Count must be between ${MIN_COUNT} and ${MAX_COUNT}.`;
    if (marks < 0 || marks > 100) return "Marks must be between 0 and 100.";
    if (extraInstructions.length > MAX_EXTRA_INSTRUCTIONS)
      return `Extra instructions ≤ ${MAX_EXTRA_INSTRUCTIONS} chars.`;
    if (topic.grade_band) {
      const overlaps = !(gradeMax < topic.grade_band.min || gradeMin > topic.grade_band.max);
      if (!overlaps)
        return `Grade band must intersect topic band ${topic.grade_band.min}–${topic.grade_band.max}.`;
    }
    return null;
  }, [gradeMin, gradeMax, count, marks, extraInstructions.length, topic.grade_band]);

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = React.useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (formError !== null) return;
      setError(null);
      setStage("loading");
      const body: AssessmentGenerateQuestionsRequest = {
        type,
        grade_band: { min: gradeMin, max: gradeMax } as AssessmentGradeBand,
        difficulty,
        language,
        count,
        marks,
        extra_instructions: extraInstructions.trim() ? extraInstructions.trim() : null,
      };
      try {
        const r = await generateMutation.mutateAsync({
          topicSlug: topic.slug,
          body,
        });
        setResult(r);
        setApprovedIds(new Set());
        setArchivedIds(new Set());
        setStage("review");
        if (r.accepted_count > 0) onSuccess?.(r.accepted_count);
      } catch (raw) {
        setError(raw);
        setStage("form");
      }
    },
    [
      formError,
      type,
      gradeMin,
      gradeMax,
      difficulty,
      language,
      count,
      marks,
      extraInstructions,
      topic.slug,
      generateMutation,
      onSuccess,
    ],
  );

  // ── Per-row actions ───────────────────────────────────────────────────
  const handleApprove = React.useCallback(
    async (q: AssessmentQuestionPublic) => {
      try {
        await approveMutation.mutateAsync({ id: q.id });
        setApprovedIds((prev) => {
          const next = new Set(prev);
          next.add(q.id);
          return next;
        });
      } catch (raw) {
        setError(raw);
      }
    },
    [approveMutation],
  );

  const handleRegenerate = React.useCallback(
    async (q: AssessmentQuestionPublic) => {
      try {
        const r = await regenerateMutation.mutateAsync({ id: q.id });
        setResult((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            generated: prev.generated.map((row) => (row.id === q.id ? r.question : row)),
          };
        });
      } catch (raw) {
        setError(raw);
      }
    },
    [regenerateMutation],
  );

  const handleArchive = React.useCallback(
    async (q: AssessmentQuestionPublic) => {
      try {
        await archiveMutation.mutateAsync({
          id: q.id,
          reason: "Rejected during AI Fill review",
        });
        setArchivedIds((prev) => {
          const next = new Set(prev);
          next.add(q.id);
          return next;
        });
      } catch (raw) {
        setError(raw);
      }
    },
    [archiveMutation],
  );

  // ── Reset on close ────────────────────────────────────────────────────
  const handleClose = React.useCallback(
    (next: boolean) => {
      if (!next) {
        // Don't clear `result` until the dialog finishes its
        // exit animation — looks cleaner than the review surface
        // popping back to the form mid-fade.
        setTimeout(() => {
          setStage("form");
          setResult(null);
          setError(null);
          setApprovedIds(new Set());
          setArchivedIds(new Set());
          setExtraInstructions("");
        }, 220);
      }
      onOpenChange(next);
    },
    [onOpenChange],
  );

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={cn("max-w-3xl", stage === "review" && "max-h-[90vh] overflow-hidden")}
        data-testid="ai-fill-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span
              className="inline-flex size-7 items-center justify-center rounded-md bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
              aria-hidden="true"
            >
              <Sparkles className="size-4" />
            </span>
            AI Fill — {topic.name}
          </DialogTitle>
          <DialogDescription>
            Generate up to {MAX_COUNT} draft questions for{" "}
            <span className="text-foreground font-medium">{topic.name}</span>. Drafts land with
            <span className="ml-1 inline-flex items-center gap-0.5 rounded border border-violet-300/60 bg-violet-50 px-1.5 py-0.5 font-mono text-[10px] text-violet-900 dark:border-violet-500/40 dark:bg-violet-950/40 dark:text-violet-200">
              origin=ai
            </span>{" "}
            and must be approved before they're drawable.
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence mode="wait" initial={false}>
          {stage === "form" && (
            <motion.form
              key="form"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              onSubmit={(e) => void handleSubmit(e)}
              className="space-y-4"
              aria-busy={generateMutation.isPending}
              data-testid="ai-fill-form"
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <FieldSelect
                  id="ai-fill-type"
                  label="Question type"
                  value={type}
                  onChange={(v) => setType(v as QuestionType)}
                >
                  {AI_GENERATABLE_QUESTION_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {QUESTION_TYPE_LABEL[t]}
                    </SelectItem>
                  ))}
                </FieldSelect>
                <FieldSelect
                  id="ai-fill-difficulty"
                  label="Difficulty"
                  value={difficulty}
                  onChange={(v) => setDifficulty(v as QuestionDifficulty)}
                >
                  {QUESTION_DIFFICULTY_ORDER.map((d) => (
                    <SelectItem key={d} value={d}>
                      {QUESTION_DIFFICULTY_LABEL[d]}
                    </SelectItem>
                  ))}
                </FieldSelect>
                <FieldSelect
                  id="ai-fill-language"
                  label="Language"
                  value={language}
                  onChange={(v) => setLanguage(v as QuestionLanguage)}
                >
                  {(["en", "hi"] as const).map((l) => (
                    <SelectItem key={l} value={l}>
                      {QUESTION_LANGUAGE_LABEL[l]}
                    </SelectItem>
                  ))}
                </FieldSelect>

                <div className="grid gap-1.5">
                  <Label htmlFor="ai-fill-count">Count (1–{MAX_COUNT})</Label>
                  <Input
                    id="ai-fill-count"
                    type="number"
                    min={MIN_COUNT}
                    max={MAX_COUNT}
                    value={count}
                    onChange={(e) =>
                      setCount(
                        Math.max(
                          MIN_COUNT,
                          Math.min(MAX_COUNT, Number(e.target.value) || DEFAULT_COUNT),
                        ),
                      )
                    }
                    aria-describedby="ai-fill-count-hint"
                    data-testid="ai-fill-count"
                  />
                  <p id="ai-fill-count-hint" className="text-muted-foreground text-[11px]">
                    Each draft costs one AI call.
                  </p>
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="ai-fill-grade-min">Grade min</Label>
                  <Input
                    id="ai-fill-grade-min"
                    type="number"
                    min={1}
                    max={12}
                    value={gradeMin}
                    onChange={(e) =>
                      setGradeMin(Math.max(1, Math.min(12, Number(e.target.value) || 1)))
                    }
                    data-testid="ai-fill-grade-min"
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="ai-fill-grade-max">Grade max</Label>
                  <Input
                    id="ai-fill-grade-max"
                    type="number"
                    min={1}
                    max={12}
                    value={gradeMax}
                    onChange={(e) =>
                      setGradeMax(Math.max(1, Math.min(12, Number(e.target.value) || 12)))
                    }
                    data-testid="ai-fill-grade-max"
                  />
                </div>

                <div className="grid gap-1.5 sm:col-span-2">
                  <Label htmlFor="ai-fill-marks">Marks per question</Label>
                  <Input
                    id="ai-fill-marks"
                    type="number"
                    min={0}
                    max={100}
                    step={0.5}
                    value={marks}
                    onChange={(e) =>
                      setMarks(Math.max(0, Math.min(100, Number(e.target.value) || 0)))
                    }
                  />
                </div>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="ai-fill-extra">
                  Extra instructions{" "}
                  <span className="text-muted-foreground text-[11px]">(optional)</span>
                </Label>
                <Textarea
                  id="ai-fill-extra"
                  rows={3}
                  maxLength={MAX_EXTRA_INSTRUCTIONS}
                  value={extraInstructions}
                  onChange={(e) => setExtraInstructions(e.target.value)}
                  placeholder="e.g. Focus on word problems involving linear equations."
                  aria-describedby="ai-fill-extra-hint"
                  data-testid="ai-fill-extra"
                />
                <p
                  id="ai-fill-extra-hint"
                  className={cn(
                    "text-[11px]",
                    extraInstructions.length > 700
                      ? "text-amber-700 dark:text-amber-300"
                      : "text-muted-foreground",
                  )}
                >
                  {extraInstructions.length}/{MAX_EXTRA_INSTRUCTIONS} characters.
                </p>
              </div>

              {formError && (
                <p
                  role="alert"
                  className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-xs"
                  data-testid="ai-fill-form-error"
                >
                  {formError}
                </p>
              )}

              {error !== null && <ErrorBlock error={error} />}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleClose(false)}
                  disabled={generateMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={formError !== null || generateMutation.isPending}
                  data-testid="ai-fill-submit"
                >
                  <Sparkles aria-hidden="true" className="size-4" />
                  Generate {count} question{count === 1 ? "" : "s"}
                </Button>
              </DialogFooter>
            </motion.form>
          )}

          {stage === "loading" && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="flex flex-col items-center justify-center gap-3 py-12"
              data-testid="ai-fill-loading"
            >
              <Loader2 aria-hidden="true" className="size-8 animate-spin text-violet-600" />
              <ShimmerText>Asking the AI counsellor…</ShimmerText>
              <p className="text-muted-foreground text-center text-xs leading-relaxed">
                Typically 2–6 seconds for {count} question{count === 1 ? "" : "s"}.
                <br />
                Please don't close this dialog — the BE call runs to completion to bill correctly.
              </p>
            </motion.div>
          )}

          {stage === "review" && result && (
            <motion.div
              key="review"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="space-y-4 overflow-y-auto pr-1"
              style={{ maxHeight: "60vh" }}
              data-testid="ai-fill-review"
            >
              <ReviewSummary result={result} />

              {result.parse_error && (
                <div
                  role="alert"
                  className="rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200"
                  data-testid="ai-fill-parse-error"
                >
                  <p className="font-medium">AI returned partially malformed output.</p>
                  <pre className="mt-1 whitespace-pre-wrap font-mono text-[11px]">
                    {result.parse_error}
                  </pre>
                </div>
              )}

              <output aria-live="polite" className="block space-y-3">
                {result.generated.length === 0 ? (
                  <div className="text-muted-foreground py-6 text-center text-xs">
                    No drafts were accepted from this generation.
                  </div>
                ) : (
                  result.generated.map((q, idx) => (
                    <GeneratedCard
                      key={q.id}
                      idx={idx + 1}
                      q={q}
                      isApproved={approvedIds.has(q.id)}
                      isArchived={archivedIds.has(q.id)}
                      onApprove={() => void handleApprove(q)}
                      onRegenerate={() => void handleRegenerate(q)}
                      onArchive={() => void handleArchive(q)}
                      isApprovePending={
                        approveMutation.isPending && approveMutation.variables?.id === q.id
                      }
                      isRegeneratePending={
                        regenerateMutation.isPending && regenerateMutation.variables?.id === q.id
                      }
                      isArchivePending={
                        archiveMutation.isPending && archiveMutation.variables?.id === q.id
                      }
                    />
                  ))
                )}
              </output>

              {result.rejection_reasons.length > 0 && (
                <details className="border-border/60 rounded-md border px-3 py-2 text-xs">
                  <summary className="cursor-pointer font-medium">
                    {result.rejection_reasons.length} draft
                    {result.rejection_reasons.length === 1 ? "" : "s"} rejected by parser
                  </summary>
                  <ul className="text-muted-foreground mt-2 list-disc space-y-1 pl-4">
                    {result.rejection_reasons.map((r, i) => (
                      <li key={i} className="font-mono">
                        {r}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {error !== null && <ErrorBlock error={error} />}

              <DialogFooter className="border-border/60 bg-background/95 sticky bottom-0 -mx-6 -mb-6 border-t px-6 py-3 backdrop-blur">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setStage("form");
                    setResult(null);
                    setApprovedIds(new Set());
                    setArchivedIds(new Set());
                  }}
                  data-testid="ai-fill-generate-more"
                >
                  <Sparkles aria-hidden="true" className="size-4" />
                  Generate more
                </Button>
                <Button type="button" onClick={() => handleClose(false)}>
                  Done
                </Button>
              </DialogFooter>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function FieldSelect({
  id,
  label,
  value,
  onChange,
  children,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="h-9" data-testid={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>{children}</SelectContent>
      </Select>
    </div>
  );
}

function ReviewSummary({ result }: { result: AssessmentGenerateQuestionsResponse }) {
  return (
    <div className="border-border/60 grid gap-2 rounded-md border bg-violet-50/40 px-3 py-2 text-xs dark:bg-violet-950/10">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-foreground font-medium" data-testid="ai-fill-counts">
          {result.accepted_count}/{result.requested_count} accepted
        </span>
        {result.rejected_count > 0 && (
          <span className="text-amber-700 dark:text-amber-300">
            {result.rejected_count} rejected
          </span>
        )}
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground font-mono text-[11px]">
          {result.provider}/{result.model}
        </span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground tabular-nums">{result.latency_ms}ms</span>
        {result.fallback_depth > 0 && <ResilienceModePill />}
      </div>
    </div>
  );
}

function GeneratedCard({
  idx,
  q,
  isApproved,
  isArchived,
  onApprove,
  onRegenerate,
  onArchive,
  isApprovePending,
  isRegeneratePending,
  isArchivePending,
}: {
  idx: number;
  q: AssessmentQuestionPublic;
  isApproved: boolean;
  isArchived: boolean;
  onApprove: () => void;
  onRegenerate: () => void;
  onArchive: () => void;
  isApprovePending: boolean;
  isRegeneratePending: boolean;
  isArchivePending: boolean;
}) {
  const dimmed = isArchived;
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, delay: Math.min(idx, 5) * 0.06 }}
      className={cn(
        "border-border/60 bg-background grid gap-2 rounded-md border px-3 py-2.5 text-sm",
        dimmed && "opacity-60",
        isApproved && "border-emerald-500/40 bg-emerald-500/5 dark:bg-emerald-500/10",
      )}
      data-testid="ai-fill-generated-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
            #{idx} · {QUESTION_TYPE_LABEL[q.type]} · {QUESTION_DIFFICULTY_LABEL[q.difficulty]} ·{" "}
            {q.grade_band.min}–{q.grade_band.max}
          </p>
          <p className="text-foreground mt-1 whitespace-pre-wrap leading-relaxed">
            {q.prompt_richtext}
          </p>
          {q.prompt_latex && (
            <pre className="bg-muted/40 mt-1.5 overflow-x-auto rounded-md px-2 py-1 font-mono text-[11px]">
              {q.prompt_latex}
            </pre>
          )}
        </div>
        {isApproved && (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300/60 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-950/40 dark:text-emerald-200">
            <CheckCircle2 aria-hidden="true" className="size-2.5" />
            Approved
          </span>
        )}
        {isArchived && (
          <span className="inline-flex items-center gap-1 rounded-full border border-rose-300/60 bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium text-rose-900 dark:border-rose-500/40 dark:bg-rose-950/40 dark:text-rose-200">
            <X aria-hidden="true" className="size-2.5" />
            Archived
          </span>
        )}
      </div>

      {q.options.length > 0 && (
        <ol className="grid gap-1 text-xs">
          {q.options.map((opt, oi) => {
            const isCorrect =
              q.correct_answer.answer_kind === "mcq_single"
                ? q.correct_answer.option_id === opt.id
                : q.correct_answer.answer_kind === "mcq_multi"
                  ? q.correct_answer.option_ids.includes(opt.id)
                  : false;
            return (
              <li
                key={opt.id}
                className={cn(
                  "border-border/40 grid gap-0.5 rounded-md border px-2 py-1",
                  isCorrect && "border-emerald-500/40 bg-emerald-500/5 dark:bg-emerald-500/10",
                )}
              >
                <span className="text-foreground">
                  <span className="text-muted-foreground font-mono text-[10px]">
                    {String.fromCharCode(65 + oi)}.
                  </span>{" "}
                  {opt.label_richtext}
                  {isCorrect && (
                    <CheckCircle2
                      aria-hidden="true"
                      className="ml-1 inline size-3 text-emerald-600"
                    />
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {!isArchived && !isApproved && (
        <div className="border-border/60 flex items-center justify-end gap-2 border-t pt-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={onRegenerate}
            disabled={isRegeneratePending}
            aria-label={`Regenerate question #${idx}`}
            data-testid="ai-fill-regenerate"
          >
            <RefreshCw
              aria-hidden="true"
              className={cn("size-3.5", isRegeneratePending && "animate-spin")}
            />
            Regenerate
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onArchive}
            disabled={isArchivePending}
            aria-label={`Archive question #${idx}`}
            data-testid="ai-fill-archive"
          >
            <Trash2 aria-hidden="true" className="size-3.5" />
            Reject
          </Button>
          <Button
            size="sm"
            onClick={onApprove}
            disabled={isApprovePending}
            aria-label={`Approve question #${idx}`}
            data-testid="ai-fill-approve"
          >
            {isApprovePending ? (
              <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
            ) : (
              <CheckCircle2 aria-hidden="true" className="size-3.5" />
            )}
            Approve
          </Button>
        </div>
      )}
    </motion.div>
  );
}

function ResilienceModePill() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-amber-300/60 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200"
      title="AI is running in resilience mode (fallback provider in use)."
      data-testid="ai-fill-fallback-pill"
    >
      <AlertTriangle aria-hidden="true" className="size-2.5" />
      Resilience mode
    </span>
  );
}

function ErrorBlock({ error }: { error: unknown }) {
  const isUnavailable = error instanceof ApiError && error.code === "AI_UNAVAILABLE";
  const message = error instanceof Error ? error.message : "Unknown error.";
  return (
    <div
      role="alert"
      className={cn(
        "flex items-start gap-2 rounded-md border px-3 py-2 text-xs",
        isUnavailable
          ? "border-amber-300/60 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200"
          : "border-destructive/40 bg-destructive/5 text-destructive",
      )}
      data-testid="ai-fill-error"
    >
      {isUnavailable ? (
        <AlertTriangle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
      ) : (
        <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          {isUnavailable ? "AI temporarily unavailable" : "Couldn't reach AI"}
        </p>
        <p className="mt-0.5 leading-relaxed">
          {isUnavailable
            ? "AI is temporarily unavailable. Try again in a minute, or author the question manually."
            : message}
        </p>
      </div>
    </div>
  );
}

function ShimmerText({ children }: { children: React.ReactNode }) {
  return (
    <motion.span
      animate={{ opacity: [0.55, 1, 0.55] }}
      transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
      className="text-muted-foreground text-sm font-medium"
    >
      {children}
    </motion.span>
  );
}
